<script setup lang="ts">
  import type { HealthcareUtilizationAnswer } from '~/generated/api-clients-generated'
  import { ProfileStore } from '~/stores/profile'
  import {
    HealthcareUtilization,
    UtilizationSurveyQuestions
  } from '~/composables/HealthcareUtilization'
  import { HealthcareUtilizationSurveyVModel } from '~/composables/HealthcareUtilizationSurveyVModel'

  const props = defineProps<{
    id: string
  }>()

  const form = ref()

  const profile = ProfileStore.use()
  const { hide } = DialogController.use()

  const {
    utilizationSelectPlaceholder,
    specLabel,
    hospLabel,
    priCareLabel,
    physicianVisitsOpts,
    specialistVisitsOpts,
    hospitalVisitsOpts
  } = HealthcareUtilization.use()

  const priCareRespVModel = HealthcareUtilizationSurveyVModel.use(
    UtilizationSurveyQuestions.PhysicianVisits.toString()
  )

  const specRespVModel = HealthcareUtilizationSurveyVModel.use(
    UtilizationSurveyQuestions.SpecialistVisits.toString()
  )

  const hospRespVModel = HealthcareUtilizationSurveyVModel.use(
    UtilizationSurveyQuestions.HospitalAdmissions.toString()
  )

  const priCareResp = ref<string | null>()
  const specResp = ref<string | null>()
  const hospResp = ref<string | null>()
  const healthcareUtilization = ref<HealthcareUtilizationAnswer[]>([])

  function updateHealthcareUtilization(
    val: string | null | undefined,
    question_id: UtilizationSurveyQuestions
  ) {
    healthcareUtilization.value = healthcareUtilization.value?.filter(
      (item) => item.questionId !== question_id.toString()
    )
    if (!_isNil(val)) {
      healthcareUtilization.value.push({ questionId: question_id.toString(), answer: val })
    }
  }

  watch(priCareResp, (val) =>
    updateHealthcareUtilization(val, UtilizationSurveyQuestions.PhysicianVisits)
  )
  watch(specResp, (val) =>
    updateHealthcareUtilization(val, UtilizationSurveyQuestions.SpecialistVisits)
  )
  watch(hospResp, (val) =>
    updateHealthcareUtilization(val, UtilizationSurveyQuestions.HospitalAdmissions)
  )

  function initialize() {
    healthcareUtilization.value = profile.needsAssessment.healthcareUtilization ?? []
    priCareResp.value = priCareRespVModel.value
    specResp.value = specRespVModel.value
    hospResp.value = hospRespVModel.value
  }

  const onSave = async () => {
    profile.needsAssessment.healthcareUtilization = healthcareUtilization.value
    await ProfileStore.save(true)
    hide(props.id)
  }
</script>

<template>
  <HpSidebar :id="id" @show="initialize" icon="lucide:cross" title="Healthcare Utilization" static>
    <HpForm id="utilization-form" ref="form" :on-submit="onSave" class="mt-5">
      <p>How many times in the last year have you:</p>

      <div class="mt-3 flex max-w-[20rem] flex-col gap-6">
        <HpFormSelect
          :placeholder="utilizationSelectPlaceholder"
          id="primary-care"
          :label="priCareLabel"
          v-model="priCareResp"
          :options="physicianVisitsOpts"
        />
        <HpFormSelect
          :placeholder="utilizationSelectPlaceholder"
          id="specialist"
          :label="specLabel"
          v-model="specResp"
          :options="specialistVisitsOpts"
        />
        <HpFormSelect
          :placeholder="utilizationSelectPlaceholder"
          id="hospital"
          :label="hospLabel"
          v-model="hospResp"
          :options="hospitalVisitsOpts"
        />
      </div>
    </HpForm>

    <template #footer>
      <div class="flex w-full gap-5">
        <HpButton variant="secondary" @click="hide(id)">Cancel</HpButton>
        <HpButton variant="primary" @click="() => form.submit()">Save</HpButton>
      </div>
    </template>
  </HpSidebar>
</template>

<style scoped></style>
